/**
 * Simple inline logger — no external debug dependency.
 * Avoids webpack dynamic-require issues with the `debug` npm package.
 *
 * Enabled when:
 *   - localStorage.getItem("DEBUG") or localStorage.getItem("VNINVEST_DEBUG") is set
 *   - process.env.DEBUG is set
 *
 * Usage:
 *   import { createLogger } from '@/lib/utils/logger';
 *   const log = createLogger('vninvestcharts-widget:adapter:binance');
 *   log.info('Fetching data for %s', symbol);
 */
const noop = () => { };
const noopLogger = {
    log: noop,
    info: noop,
    warn: noop,
    error: noop,
    debug: noop,
    trace: noop,
    extend: () => noopLogger,
};
function isDebugEnabled() {
    if (typeof process !== "undefined" && process.env?.DEBUG)
        return true;
    try {
        if (typeof localStorage !== "undefined") {
            return !!(localStorage.getItem("DEBUG") || localStorage.getItem("VNINVEST_DEBUG"));
        }
    }
    catch { /* localStorage may throw in SSR */ }
    return false;
}
const enabled = isDebugEnabled();
function createLogFn(namespace, level) {
    if (!enabled)
        return noop;
    return (...args) => {
        console[level](`[${namespace}]`, ...args);
    };
}
export function createLogger(namespace) {
    if (!enabled)
        return noopLogger;
    return {
        log: createLogFn(namespace, "log"),
        info: createLogFn(namespace, "info"),
        warn: createLogFn(namespace, "warn"),
        error: createLogFn(namespace, "error"),
        debug: createLogFn(namespace, "debug"),
        trace: createLogFn(namespace, "trace"),
        extend: (sub) => createLogger(`${namespace}:${sub}`),
    };
}
// Default logger for the package
export const logger = createLogger('vninvestcharts-widget');
// Convenience functions for common patterns
export function createComponentLogger(componentName) {
    return createLogger(`vninvestcharts-widget:component:${componentName}`);
}
export function createAdapterLogger(adapterName) {
    return createLogger(`vninvestcharts-widget:adapter:${adapterName}`);
}
export function createHookLogger(hookName) {
    return createLogger(`vninvestcharts-widget:hook:${hookName}`);
}
export function createEngineLogger(enginePart) {
    return createLogger(`vninvestcharts-widget:engine:${enginePart}`);
}
